import React, { useState } from 'react';
import { Typography, ButtonPro } from 'components';
import { agent } from 'services/service';
import { PublicHolidayUploadResponse } from 'services/PublicHolidayService';
import { toast } from 'react-toastify';
import './index.scss';

export const PublicHolidayUploadForm = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<PublicHolidayUploadResponse | null>(
    null
  );
  const [showResults, setShowResults] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file extension
      if (!file.name.endsWith('.xlsx')) {
        toast.error('Only .xlsx files are allowed');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setResult(null);
      setShowResults(false);
    }
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value);
    if (value >= 2020 && value <= 2100) {
      setYear(value);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.error('Please select an Excel file');
      return;
    }

    if (year < 2020 || year > 2100) {
      toast.error('Please enter a valid year between 2020 and 2100');
      return;
    }

    setIsUploading(true);
    setResult(null);
    setShowResults(false);

    try {
      const response = await agent.PublicHolidayService.uploadPublicHolidays(
        selectedFile,
        year
      );
      const data = response.data;
      setResult(data);
      setShowResults(true);

      // Show appropriate toast notification
      if (data.status === 'Error') {
        toast.error(data.message);
      } else if (data.status === 'Warning') {
        toast.warning(data.message);
      } else {
        toast.success(data.message);
      }

      setSelectedFile(null);
      // Reset file input
      const fileInput = document.getElementById(
        'file-upload'
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'An error occurred while uploading the file';
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="public-holiday-upload-wrapper">
      <div className="header-section">
        <Typography type="heading2" text="Public Holiday Upload" />
        <Typography
          type="base_regular"
          text="Upload an Excel file to add public holidays for the year"
        />
      </div>

      <div className="form-section">
        <div className="form-group">
          <label htmlFor="year-input" className="form-label">
            <Typography type="base_medium" text="Year" />
          </label>
          <input
            id="year-input"
            type="number"
            className="year-input"
            value={year}
            onChange={handleYearChange}
            min="2020"
            max="2100"
            disabled={isUploading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="file-upload" className="form-label">
            <Typography type="base_medium" text="Excel File" />
          </label>
          <div className="file-input-wrapper">
            <input
              id="file-upload"
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              disabled={isUploading}
              className="file-input"
            />
            {selectedFile && (
              <Typography
                type="small_regular"
                weight="bold"
                text={`Selected: ${selectedFile.name}`}
              />
            )}
          </div>
          <Typography
            type="small_regular"
            text="Note: Excel file must contain columns: Month, Date, Holiday, Event, Applicability."
          />
        </div>

        <div className="button-group">
          <ButtonPro
            label={isUploading ? 'Uploading...' : 'Submit'}
            onClick={handleSubmit}
            disabled={isUploading || !selectedFile}
            htmlType="button"
            type="primary"
            isLoading={isUploading}
          />
        </div>
      </div>

      {showResults && result && (
        <div
          className={`result-section ${
            result.status.toLowerCase() === 'error' ? 'error' : 'success'
          }`}
        >
          <div className="result-header">
            <Typography type="base_medium" text="Upload Details" />
            <button
              className="close-btn"
              onClick={() => setShowResults(false)}
              type="button"
            >
              ×
            </button>
          </div>

          {result.totalRowsInExcel > 0 && (
            <div className="result-stats">
              <div className="stat-item">
                <Typography type="small_medium" text="Holiday Data Rows:" />
                <Typography
                  type="small_regular"
                  text={result.totalRowsInExcel.toString()}
                />
              </div>
              <div className="stat-item">
                <Typography type="small_medium" text="Successfully Uploaded:" />
                <Typography
                  type="small_regular"
                  text={result.successfulUploads.toString()}
                />
              </div>
              <div className="stat-item">
                <Typography type="small_medium" text="Skipped:" />
                <Typography
                  type="small_regular"
                  text={result.skippedRows.toString()}
                />
              </div>
            </div>
          )}

          {result.errors && result.errors.length > 0 && (
            <div className="errors-list">
              <Typography type="small_medium" text="Errors:" />
              <ul>
                {result.errors.map((err, index) => (
                  <li key={index}>
                    <Typography type="small_regular" text={err} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.skippedHolidays && result.skippedHolidays.length > 0 && (
            <div className="skipped-list">
              <Typography type="small_medium" text="Skipped Holidays:" />
              <ul style={{ marginTop: '1rem' }}>
                {result.skippedHolidays.map((holiday, index) => (
                  <li key={index}>
                    <Typography type="small_regular" text={holiday} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
